import { isNil } from "lopermut"
import * as React from "react"
import { cn } from "../utils"

const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea"> & {
    disabled?: boolean
    maxLength?: number
    maxLengthClassName?: string
  }
>(({ className, disabled, maxLength, maxLengthClassName, value, onChange, ...props }, ref) => {
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null)
  const combinedRef = React.useCallback(
    (node: HTMLTextAreaElement) => {
      if (typeof ref === "function") {
        ref(node)
      } else if (ref) {
        ;(ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node
      }
      innerRef.current = node
    },
    [ref],
  )

  let trimmedValue = value
  if (!isNil(maxLength) && typeof value === "string") {
    trimmedValue = value.slice(0, maxLength)
  }

  React.useLayoutEffect(() => {
    const textarea = innerRef.current
    if (textarea) {
      textarea.style.height = "auto"
      textarea.style.height = textarea.scrollHeight + "px"
    }
  }, [trimmedValue])

  function onChangeWithLength(event: React.ChangeEvent<HTMLTextAreaElement>) {
    if (isNil(maxLength)) {
      onChange?.(event)
    } else {
      if (event.target.value.length > maxLength) {
        event.target.value = event.target.value.slice(0, maxLength)
      }
      onChange?.(event)
    }

    const textarea = innerRef.current
    if (textarea) {
      textarea.style.height = "auto"
      textarea.style.height = textarea.scrollHeight + "px"
    }
  }

  const content = (
    <textarea
      data-slot="textarea"
      className={cn(
        "flex w-full resize-none min-h-20 rounded-md border border-input bg-background dark:bg-input/30 px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
        className,
      )}
      {...props}
      value={trimmedValue}
      ref={combinedRef}
      disabled={disabled}
      onChange={onChangeWithLength}
      rows={1}
      style={{ overflow: "auto", ...props.style }}
    />
  )

  if (isNil(maxLength)) return content

  const valueLength = typeof value === "string" ? value.length : undefined
  const showCount = typeof value === "string"
  const countIsCloseToMax = showCount && valueLength && valueLength > maxLength * 0.9

  return (
    <div className="flex flex-col items-start gap-0.5">
      <div className="w-full">{content}</div>
      {showCount && (
        <span
          className={cn(
            "text-xs text-muted-foreground",
            countIsCloseToMax && "text-error",
            maxLengthClassName,
          )}
        >
          {valueLength}/{maxLength}
        </span>
      )}
    </div>
  )
})

Textarea.displayName = "Textarea"

export { Textarea }
